import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnalysisModule from '../components/AnalysisModule';
import RimlandMap from '../components/RimlandMap';
import ExpandableCard from '../components/ExpandableCard';
import History from '../components/History';
import { initFirebase, saveAnalysis, subscribeToHistory } from '../firebase';

const Spykman = () => {
  const [userId, setUserId] = useState(null);
  const [authStatus, setAuthStatus] = useState('Cargando autenticación...');
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    const init = async () => {
      const uid = await initFirebase();
      if (uid) {
        setUserId(uid);
        setAuthStatus(`✅ Conectado`);
        
        const unsubscribe = subscribeToHistory(uid, (data) => {
          setAnalyses(data);
        });
        
        return () => unsubscribe();
      } else {
        setAuthStatus('⚠️ No hay usuario autenticado');
      }
    };
    
    init();
  }, []);

  const handleSave = (query, result, type) => {
    if (userId) {
      saveAnalysis(userId, query, result, type);
    }
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/">
          <button style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            background: 'rgba(30, 41, 59, 0.8)',
            color: '#cbd5e1',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}>
            ← Volver al Inicio
          </button>
        </Link>
      </div>
      <Header 
        title="Teoría del Rimland" 
        subtitle="Nicholas Spykman y el Control de las Zonas Costeras"
        authStatus={authStatus}
      />

      <section className="card" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004e92' }}>La Tesis Central</h2>
        <p style={{ fontSize: '1.5rem', fontWeight: 'black', fontStyle: 'italic', margin: '1rem 0', color: '#000428' }}>
          "Quien controle el Rimland dominará Eurasia; quien domine Eurasia controlará los destinos del mundo."
        </p>
        <p style={{ color: '#94a3b8' }}>- Nicholas Spykman, 1944</p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#000428' }}>
          ¿Qué es el Rimland?
        </h2>
        <div className="grid">
          <ExpandableCard
            icon="🌊"
            title="Definición del Rimland"
            color="#3b82f6"
            summary="El Rimland es la zona costera de Eurasia que rodea al Heartland, actuando como zona de amortiguamiento entre el poder terrestre y el poder marítimo."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Regiones que componen el Rimland:</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold', color: '#3b82f6' }}>🇪🇺 Europa Occidental</p>
                    <p style={{ fontSize: '0.85rem' }}>Alemania, Francia, Italia, España</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold', color: '#8b5cf6' }}>🕌 Medio Oriente</p>
                    <p style={{ fontSize: '0.85rem' }}>Turquía, Irán, Arabia, Levante</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold', color: '#eab308' }}>🇮🇳 Asia del Sur</p>
                    <p style={{ fontSize: '0.85rem' }}>India, Pakistán, Bangladesh</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold', color: '#ef4444' }}>🇨🇳 Asia Oriental</p>
                    <p style={{ fontSize: '0.85rem' }}>China, Corea, Japón, Sudeste Asiático</p>
                  </div>
                </div>
                <p style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #22c55e' }}>
                  💡 <strong>Clave:</strong> El Rimland contiene el 70% de la población mundial y el 80% de la actividad económica global.
                </p>
              </div>
            }
          />

          <ExpandableCard
            icon="⚖️"
            title="Poder Anfibio"
            color="#8b5cf6"
            summary="El Rimland tiene acceso tanto a tierra como a mar, lo que le da una ventaja estratégica única sobre el Heartland."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Ventajas del Rimland:</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>Acceso marítimo:</strong> Comercio global y proyección de poder naval</li>
                  <li><strong>Densidad poblacional:</strong> Mayor fuerza laboral y mercados</li>
                  <li><strong>Desarrollo industrial:</strong> Centros tecnológicos y manufactureros</li>
                  <li><strong>Flexibilidad estratégica:</strong> Puede defenderse por tierra y mar</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>🎯 Estrategia de Spykman:</p>
                  <p style={{ fontSize: '0.9rem' }}>
                    Controlar el Rimland permite contener al Heartland y evitar que una potencia terrestre domine Eurasia.
                  </p>
                </div>
              </div>
            }
          />
        </div>
      </section>

      <section className="card" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '1rem', color: '#004e92' }}>
          🗺️ Visualización del Rimland
        </h2>
        <p style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#cbd5e1' }}>
          Mapa interactivo mostrando las zonas del Rimland que rodean al Heartland. Incluye puntos estratégicos clave de la Guerra Fría.
        </p>
        <RimlandMap />
        <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.9rem', color: '#94a3b8', fontStyle: 'italic' }}>
          💡 Haz clic en las zonas coloreadas y puntos rojos para ver más información
        </p>
      </section>

      <section className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#004e92', textAlign: 'center' }}>
          🔄 La Inversión de Mackinder
        </h3>
        <p style={{ textAlign: 'center', marginBottom: '1.5rem', lineHeight: '1.7' }}>
          Spykman invirtió la fórmula de Mackinder, argumentando que el Rimland, no el Heartland, era la clave del poder mundial.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          <div style={{ padding: '1.5rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.75rem', borderLeft: '4px solid #3b82f6' }}>
            <p style={{ fontWeight: 'bold', color: '#3b82f6', marginBottom: '0.5rem' }}>Mackinder decía:</p>
            <p style={{ fontStyle: 'italic', fontSize: '0.95rem' }}>
              "Quien controle el Heartland controlará la Isla-Mundo"
            </p>
          </div>
          <div style={{ padding: '1.5rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.75rem', borderLeft: '4px solid #8b5cf6' }}>
            <p style={{ fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem' }}>Spykman respondió:</p>
            <p style={{ fontStyle: 'italic', fontSize: '0.95rem' }}>
              "Quien controle el Rimland controlará Eurasia; quien controle Eurasia controlará los destinos del mundo"
            </p>
          </div>
        </div>
      </section>

      <AnalysisModule
        title="✨ Análisis Rimland"
        description="Analice un escenario geopolítico desde la perspectiva de la Teoría del Rimland de Spykman."
        inputType="textarea"
        category="spykman"
        onSave={handleSave}
        type="scenario"
      />

      <AnalysisModule
        title="📚 Conceptos del Rimland"
        description="Seleccione un concepto para obtener su definición."
        inputType="select"
        options={[
          'Rimland Theory',
          'Containment'
        ]}
        category="spykman"
        onSave={handleSave}
        type="concept"
      />

      <History analyses={analyses} />

      <div style={{ position: 'fixed', bottom: '20px', right: '20px', display: 'flex', gap: '10px', zIndex: 1000 }}>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          🏠 Inicio
        </Link>
        <Link to="/rimland2" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Siguiente ➡️
        </Link>
      </div>
    </div>
  );
};

export default Spykman;
